/**
 * Shared disability SETA utils (frontend + edge functions).
 * Canonical file: supabase/functions/common/utils/disabilityStatusUtils.js
 * This copy exists for CRA (imports must live under src/).
 * When changing logic, update both files (keep them identical).
 */

function normalizeDisabilityValue(s) {
  if (s == null || s === "") return "";
  return String(s).toLowerCase().trim().replace(/\s+/g, " ");
}

function isStructuredDisabilityOptions(options) {
  return (
    Array.isArray(options) &&
    options.length > 0 &&
    options.every(
      (o) => o != null && typeof o === "object" && "isDisabilityType" in o,
    )
  );
}

function getDisabilityFieldName(fields) {
  if (!Array.isArray(fields)) return null;
  return fields.find((f) => f?.config?.isDisabilityField)?.fieldName ?? null;
}

function getDisabilityValueFromRecord(record, disabilityFieldName) {
  if (!record || !disabilityFieldName) return null;
  const fromData = record.data?.[disabilityFieldName];
  if (fromData != null && fromData !== "") return fromData;
  const flat = record[disabilityFieldName];
  if (flat != null && flat !== "") return flat;
  return null;
}

function getTemplateParts(template) {
  if (!template) return { sheet: undefined, fileStructure: undefined, fields: [] };
  const sheet = template?.sheets?.[0];
  if (Array.isArray(sheet?.fields)) {
    return { sheet, fileStructure: template, fields: sheet.fields };
  }
  if (Array.isArray(template?.fields)) {
    return { sheet: undefined, fileStructure: template, fields: template.fields };
  }
  if (Array.isArray(template)) {
    return { sheet: undefined, fileStructure: undefined, fields: template };
  }
  return { sheet: undefined, fileStructure: undefined, fields: [] };
}

function resolveOptionsFromTemplateParts({ sheet, fileStructure, fields } = {}) {
  let disabilityOptions =
    Array.isArray(sheet?.disabilityTypes) && sheet.disabilityTypes.length > 0
      ? sheet.disabilityTypes
      : Array.isArray(fileStructure?.disabilityTypes)
        ? fileStructure.disabilityTypes
        : [];

  if (!disabilityOptions.length && Array.isArray(fields)) {
    const allowed = fields.find((f) => f?.config?.isDisabilityField)?.allowedValues;
    if (isStructuredDisabilityOptions(allowed)) {
      disabilityOptions = allowed;
    }
  }

  return disabilityOptions;
}

function matchDisabilityValue(disabilityOptions, disabilityValue) {
  if (disabilityValue == null || disabilityValue === "") return false;
  if (!isStructuredDisabilityOptions(disabilityOptions)) return false;

  const normalized = normalizeDisabilityValue(disabilityValue);
  const entry = disabilityOptions.find((item) => {
    const value = normalizeDisabilityValue(item?.value);
    const label = normalizeDisabilityValue(item?.label);
    return value === normalized || label === normalized;
  });

  return entry ? Boolean(entry.isDisabilityType) : false;
}

/** SETA template → { disabilityOptions, disabilityFieldName }. */
export function getDisabilityConfig({
  sheet,
  fileStructure,
  fields,
  disabilityTypes,
  template,
} = {}) {
  const parts = template
    ? getTemplateParts(template)
    : { sheet, fileStructure, fields: fields ?? [] };

  const disabilityOptions = isStructuredDisabilityOptions(disabilityTypes)
    ? disabilityTypes
    : resolveOptionsFromTemplateParts(parts);

  return {
    disabilityOptions,
    disabilityFieldName: getDisabilityFieldName(parts.fields),
  };
}

/** Parse file_structure JSON (string or object) → disability config. */
export function getDisabilityConfigFromFileStructure(fileStructure) {
  if (!fileStructure) {
    return { disabilityOptions: [], disabilityFieldName: null };
  }

  let parsed = fileStructure;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { disabilityOptions: [], disabilityFieldName: null };
    }
  }

  const sheet = parsed?.sheets?.[0];
  return getDisabilityConfig({
    sheet,
    fileStructure: parsed,
    fields: sheet?.fields ?? [],
  });
}

/**
 * Single resolver for disability boolean.
 * Write: pass `value`. Read: pass `record` (falls back to disability_status column).
 */
export function resolveDisability({
  record,
  value,
  disabilityOptions,
  disabilityFieldName,
  fields,
  disabilityTypes,
  sheet,
  fileStructure,
  template,
} = {}) {
  const config = getDisabilityConfig({
    sheet,
    fileStructure,
    fields,
    disabilityTypes,
    template,
  });
  const options = disabilityOptions ?? config.disabilityOptions;

  if (record) {
    const fieldName = disabilityFieldName ?? config.disabilityFieldName;
    const rawValue = getDisabilityValueFromRecord(record, fieldName);

    if (rawValue != null && rawValue !== "" && isStructuredDisabilityOptions(options)) {
      return matchDisabilityValue(options, rawValue);
    }

    if (record.disability_status != null) {
      return record.disability_status === true;
    }

    return false;
  }

  return matchDisabilityValue(options, value);
}
